import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { GuestAlbumPicker } from "./GuestAlbumPicker";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const toBody = (f) => ({
  name: f.name, email: f.email || null,
  month: f.month ? Number(f.month) : null, day: f.month && f.day ? Number(f.day) : null,
  birth_year: f.birth_year ? Number(f.birth_year) : null,
  nicknames: f.nicknames.split(",").map((s) => s.trim()).filter(Boolean), user_id: f.user_id || null,
});

const GuestFields = ({ g, setG, hasEmail }) => (
  <div className="people-guest" data-testid="person-guest-fields">
    <div className="flex gap-2 items-center">
      <label className="text-xs text-neutral-600">Days</label>
      <select className="login-input !w-24" value={g.days} onChange={(e) => setG({ ...g, days: Number(e.target.value) })} data-testid="person-guest-days">
        {[7, 6, 5, 4, 3, 2, 1].map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
      <input className="login-input" placeholder="Guest username" value={g.username} onChange={(e) => setG({ ...g, username: e.target.value })} data-testid="person-guest-username" />
    </div>
    <GuestAlbumPicker mode={g.mode} ids={g.ids} onChange={(mode, ids) => setG({ ...g, mode, ids })} testid="person-guest-albums" />
    <label className="text-sm flex gap-2 items-center mt-2">
      <input type="checkbox" disabled={!hasEmail} checked={g.email && hasEmail} onChange={(e) => setG({ ...g, email: e.target.checked })} data-testid="person-guest-email-checkbox" />
      Email the link to this person{!hasEmail && " (add an email first)"}
    </label>
  </div>
);

export const PersonEditor = ({ initial, users, onSave, onCancel, chooseKind = false }) => {
  const [f, setF] = useState(initial);
  const [kind, setKind] = useState("guest");
  const [guestToggle, setGuestOn] = useState(false);
  const guestOn = chooseKind ? kind === "guest" : guestToggle;
  const [g, setG] = useState({ days: 7, username: "", mode: "all", ids: [], email: true });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const canGuest = !chooseKind && !initial.user_id && (!initial.guest || initial.guest.status !== "active");
  useEffect(() => {
    if (guestOn && !g.username && f.name.trim()) api.get("/people/suggest-username", { params: { name: f.name } }).then((r) => setG((x) => ({ ...x, username: r.data.username })));
  }, [guestOn]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = (e) => {
    e.preventDefault();
    onSave(toBody(f), guestOn ? { days: g.days, username: g.username || null, album_mode: g.mode, album_ids: g.ids, send_email: g.email && !!f.email } : null);
  };
  return (
    <form className="people-form" onSubmit={submit} data-testid="person-form">
      <input className="login-input" placeholder="Name (e.g. Emma)" required value={f.name} onChange={set("name")} data-testid="person-name-input" />
      {chooseKind && (
        <div className="dest-switch" data-testid="person-kind">
          <button type="button" className={kind === "guest" ? "on" : ""} onClick={() => setKind("guest")} data-testid="person-kind-guest">Guest access (up to 7 days)</button>
          <button type="button" className={kind === "birthday" ? "on" : ""} onClick={() => setKind("birthday")} data-testid="person-kind-birthday">Birthday only (no login)</button>
        </div>
      )}
      <input className="login-input" type="email" placeholder="Email (optional)" value={f.email} onChange={set("email")} data-testid="person-email-input" />
      <div className="flex gap-2">
        <select className="login-input" value={f.month} onChange={set("month")} required={chooseKind && kind === "birthday"} data-testid="person-month-select">
          <option value="">No birthday</option>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <input className="login-input" type="number" min="1" max="31" placeholder="Day" required={!!f.month} disabled={!f.month} value={f.day} onChange={set("day")} data-testid="person-day-input" />
        <input className="login-input" type="number" min="1900" max="2100" placeholder="Birth year (optional)" value={f.birth_year} onChange={set("birth_year")} data-testid="person-year-input" />
      </div>
      <input className="login-input" placeholder="Nicknames, comma separated (optional)" value={f.nicknames} onChange={set("nicknames")} data-testid="person-nicknames-input" />
      {!chooseKind && (
        <select className="login-input" value={f.user_id} onChange={set("user_id")} data-testid="person-user-select">
          <option value="">No linked account</option>
          {users.map((u) => <option key={u.user_id} value={u.user_id}>{`${u.name} · ${u.email}`}</option>)}
        </select>
      )}
      {canGuest && (
        <label className="text-sm flex gap-2 items-center" data-testid="person-guest-toggle-label">
          <input type="checkbox" checked={guestOn} onChange={(e) => setGuestOn(e.target.checked)} data-testid="person-guest-toggle" />
          Give guest access (view and download only, up to 7 days)
        </label>
      )}
      {guestOn && <GuestFields g={g} setG={setG} hasEmail={!!f.email} />}
      <div className="flex gap-2">
        <button className="btn-primary !py-1.5 !px-4 text-sm" data-testid="person-save-button">Save</button>
        <button type="button" className="btn-outline !py-1.5 !px-4 text-sm" onClick={onCancel} data-testid="person-cancel-button">Cancel</button>
      </div>
    </form>
  );
};

export const EMPTY_PERSON = { name: "", email: "", month: "", day: "", birth_year: "", nicknames: "", user_id: "" };
export const editable = (p) => ({ ...p, email: p.email || "", month: p.month || "", day: p.day || "", birth_year: p.birth_year || "", nicknames: (p.nicknames || []).join(", "), user_id: p.user_id || "" });
